import React, { useState, useEffect, useRef } from "react";
import { FaArrowLeft, FaUser } from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getRequest, putRequest } from "../../helpers/api.helper";
import Loader from "../../components/Loader";
import ServicePreferenceModal from "./modals/ServicePreferenceModal";
import { PackageDetailsType, ProfileType } from "../../types";
import Gnavbar from "../../components/Gnavbar";
import BottomNavbar from "../../components/BottomNavbar";
import ImagePrivacyModal from "./modals/ImagePrivacyModal";
import DownloadModal from "./modals/DownloadModal";
import { convertStringToBoolean } from "../../helpers/function.helper";
import PrivacyModal from "./modals/PrivacyModal";
import SettingsModal from "./modals/SettingsModal";
import SuspendModal from "./modals/SuspendModal";
import ShortListModal from "./modals/ShortlistModal";
import SendInterestModal from "./modals/SendInterestModal";
import PasswordModal from "./modals/PasswordModal";
import EmailModal from "./modals/EmailModal";
import BiodataPreview from "./BiodataPreview";
import ProfileHistoryModal from "./modals/ProfileHistoryModal";
import ProfileActions from "./ProfileActions";
import ProfileHeader from "./ProfileHeader";
import DeleteModal from "./modals/DeleteModal";

const useModal = () => {
  const [isPasswordModalOpen, setIsPasswordModalOpen] = React.useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = React.useState(false);
  const [currentPassword, setCurrentPassword] = React.useState("");
  const [currentEmail, setCurrentEmail] = React.useState("");

  const openPasswordModal = (password: any) => {
    setCurrentPassword(password);
    setIsPasswordModalOpen(true);
  };

  const openEmailModal = (email: any) => {
    setCurrentEmail(email);
    setIsEmailModalOpen(true);
  };

  const closePasswordModal = () => {
    setIsPasswordModalOpen(false);
    setCurrentPassword("");
  };

  const closeEmailModal = () => {
    setIsEmailModalOpen(false);
    setCurrentEmail("");
  };

  return {
    isPasswordModalOpen,
    currentPassword,
    openPasswordModal,
    closePasswordModal,
    isEmailModalOpen,
    currentEmail,
    openEmailModal,
    closeEmailModal,
  };
};

const ProfileOptions = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo =
    (location.state as { returnTo?: string } | null)?.returnTo || null;

  const [profile, setProfile] = useState<ProfileType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [shortlistedProfiles, setShortlistedProfiles] = useState<Set<string>>(
    new Set()
  );

  // Modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isShortlistModalOpen, setIsShortlistModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isSendInterestModalOpen, setIsSendInterestModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isSuspendModalOpen, setIsSuspendModalOpen] = useState(false);
  const [isImagePrivacyModalOpen, setIsImagePrivacyModalOpen] = useState(false);

  const imagePrivacyOptions: any = [
    { value: "all", label: "Publish to All Members" },
    { value: "acceptedOnly", label: "Only Accepted Members" },
    { value: "blurred", label: "Do Not Publish" },
  ];

  // Service preference modal states
  const [isServicePreferenceModalOpen, setIsServicePreferenceModalOpen] =
    useState(false);
  const [packageDetails, setPackageDetails] =
    useState<PackageDetailsType | null>(null);
  const [packageLoading, setPackageLoading] = useState(false);

  // Use the custom modal hook
  const {
    isPasswordModalOpen,
    currentPassword,
    openPasswordModal,
    closePasswordModal,
    isEmailModalOpen,
    currentEmail,
    openEmailModal,
    closeEmailModal,
  } = useModal();

  // Add a ref for the profile card
  const profileCardRef = useRef(null);
  // Add a ref for the biodata preview

  // Calculate age

  // Service preference functions
  const openServicePreferenceModal = () => {
    setIsServicePreferenceModalOpen(true);
  };

  const closeServicePreferenceModal = () => {
    setIsServicePreferenceModalOpen(false);
  };

  const handleServicePreferenceSuccess = () => {
    // Refresh profile data to get updated service preference
    if (profileId) {
      fetchProfile();
      fetchPackageDetails();
    }
  };

  const fetchPackageDetails = async () => {
    if (!profileId) return;

    try {
      setPackageLoading(true);
      const response = await getRequest(
        `/bureau/packages/profile/${profileId}`
      );
      if (response.success) {
        setPackageDetails(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching package details:", error);
    } finally {
      setPackageLoading(false);
    }
  };

  // Fetch profile data
  const fetchProfile = async () => {
    try {
      const response = await getRequest(`/bureau/profile/${profileId}`);
      if (response.data) {
        const processedProfile = {
          ...response.data,
          visibleToAll: convertStringToBoolean(response.data.visibleToAll),
          showOtherProfiles: convertStringToBoolean(
            response.data.showOtherProfiles
          ),
        };
        setProfile(processedProfile);
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      setError("Error fetching profile data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (profileId) {
      fetchProfile();
    }
  }, [profileId]);

  const isProfileShortlisted = (profileId: string) => {
    return shortlistedProfiles.has(profileId);
  };

  // Modal handlers
  const openPrivacyModal = () => setIsPrivacyModalOpen(true);
  const closePrivacyModal = () => setIsPrivacyModalOpen(false);
  const closeSettingsModal = () => setIsSettingsModalOpen(false);
  const openSuspendModal = () => setIsSuspendModalOpen(true);
  const closeSuspendModal = () => setIsSuspendModalOpen(false);

  const openDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const closeReportModal = () => {
    setIsDeleteModalOpen(false);
  };

  const closeShortlistModal = () => {
    setIsShortlistModalOpen(false);
  };

  const closeSendInterestModal = () => {
    setIsSendInterestModalOpen(false);
  };


  // Handler to toggle activeStatus
  const handleToggleActiveStatus = async () => {
    if (!profile) return;
    const newStatus = !(profile.activeStatus === true);
    try {
      const response = await putRequest(`/bureau/profile/${profile?._id}`, {
        activeStatus: newStatus,
      });
      if (response.success) {
        setProfile((prev) =>
          prev ? { ...prev, activeStatus: newStatus } : prev
        );
        toast.success(`Profile is now ${newStatus ? "Active" : "Inactive"}`);
      } else {
        toast.error(response.message || "Failed to update status");
      }
    } catch (error) {
      toast.error("Failed to update active status.");
    }
  };

  // Add state for image privacy modal

  // Add state for profile history modal
  const [isProfileHistoryModalOpen, setIsProfileHistoryModalOpen] =
    useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen p-4 bg-gray-100">
        <div className="flex items-center mb-4">
          <FaArrowLeft
            className="mr-2 text-gray-600 cursor-pointer"
            onClick={() => navigate(-1)}
          />
          <h1 className="text-2xl font-semibold">Profile Options</h1>
        </div>
        <p className="text-red-500">{error || "Profile not found"}</p>
      </div>
    );
  }

  return (
    <div className="mb-14">
      <Gnavbar />
      <div className="min-h-screen p-4 bg-gray-100 text-gray-800">
        <ProfileHeader profile={profile} profileCardRef={profileCardRef} />
        <ProfileActions
          profile={profile}
          handleToggleActiveStatus={handleToggleActiveStatus}
          openPrivacyModal={openPrivacyModal}
          openDeleteModal={openDeleteModal}
          openPasswordModal={openPasswordModal}
          openServicePreferenceModal={openServicePreferenceModal}
          openSuspendModal={openSuspendModal}
          imagePrivacyOptions={imagePrivacyOptions}
          setIsDownloadModalOpen={setIsDownloadModalOpen}
          setIsImagePrivacyModalOpen={setIsImagePrivacyModalOpen}
        />

        {/* View Full Profile Button */}
        <div className="mt-6">
          <a
            href={`/admin/profile/detail/${profile.martialId}`}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg flex items-center justify-center hover:bg-blue-700 transition-colors"
          >
            <FaUser className="mr-2" size={20} />
            View Full Profile
          </a>
        </div>
        {/* All Modals */}
        {/* Profile Privacy Modal */}
        {isPrivacyModalOpen && (
          <PrivacyModal
            profile={profile}
            closePrivacyModal={closePrivacyModal}
            setProfile={setProfile}
          />
        )}
        {/* Settings Modal */}
        {isSettingsModalOpen && (
          <SettingsModal
            closeSettingsModal={closeSettingsModal}
            profile={profile}
          />
        )}
        {/* Report Modal */}
        {isDeleteModalOpen && (
          <DeleteModal
            profile={profile}
            closeReportModal={closeReportModal}
            returnTo={returnTo}
          />
        )}

        {/* Suspend Modal */}
        {isSuspendModalOpen && (
          <SuspendModal
            profile={profile}
            closeSuspendModal={closeSuspendModal}
            setIsSuspendModalOpen={setIsSuspendModalOpen}
          />
        )}
        {/* Shortlist Modal - Only show if profile is not already shortlisted */}
        {isShortlistModalOpen && !isProfileShortlisted(profile._id) && (
          <ShortListModal
            profile={profile}
            closeShortlistModal={closeShortlistModal}
            setShortlistedProfiles={setShortlistedProfiles}
          />
        )}
        {/* Send Interest Modal */}
        {isSendInterestModalOpen && (
          <SendInterestModal
            profile={profile}
            closeSendInterestModal={closeSendInterestModal}
          />
        )}
        {/* Biodata Download Modal */}
        {isDownloadModalOpen && (
          <DownloadModal
            profile={profile}
            setIsDownloadModalOpen={setIsDownloadModalOpen}
          />
        )}
        {/* Image Privacy Modal */}
        {isImagePrivacyModalOpen && (
          <ImagePrivacyModal
            profile={profile}
            setIsImagePrivacyModalOpen={setIsImagePrivacyModalOpen}
            setProfile={setProfile}
            imagePrivacyOptions={imagePrivacyOptions}
          />
        )}
        {/* Modal Components */}
        <PasswordModal
          isOpen={isPasswordModalOpen}
          password={currentPassword}
          onClose={closePasswordModal}
        />
        <EmailModal
          isOpen={isEmailModalOpen}
          email={currentEmail}
          onClose={closeEmailModal}
        />
        {/* Service Preference Modal */}
        <ServicePreferenceModal
          isOpen={isServicePreferenceModalOpen}
          onClose={closeServicePreferenceModal}
          profile={profile}
          onSuccess={handleServicePreferenceSuccess}
        />
        {/* At the end of the main render, add the hidden preview for image export */}
        <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
          <BiodataPreview profile={profile} />
        </div>
        {/* Profile History Modal */}
        {isProfileHistoryModalOpen && (
          <ProfileHistoryModal
            profile={profile}
            setIsProfileHistoryModalOpen={setIsProfileHistoryModalOpen}
            packageDetails={packageDetails}
          />
        )}
      </div>
      <BottomNavbar />
    </div>
  );
};

export default ProfileOptions;
